import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { NgxSonnerToaster } from 'ngx-sonner';
import { ImportProgressToastComponent } from './shared/components/import-progress-toast/import-progress-toast.component';
import { TwilioCallControlsComponent } from './modules/workspace/calls/components/twilio-call-controls.component';
import { SocketService } from './core/services/socket.service';
import { AuthService } from './core/services/auth.service';
import { ThemeService } from './core/services/theme.service';

@Component({
  standalone: true,
  selector: 'app-root',
  imports: [RouterOutlet, NgxSonnerToaster, ImportProgressToastComponent, TwilioCallControlsComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private firstLoadDone = false;
  private sessionCheckInterval: any;

  constructor(
    private router: Router,
    private socketService: SocketService,
    private authService: AuthService,
    private themeService: ThemeService
  ) {
    this.router.events.subscribe((event: any) => {
      const loader = document.getElementById('global-loader');
      if (!loader) return;

      // Only apply loader logic during first bootstrap navigation
      if (!this.firstLoadDone) {
        if (event instanceof NavigationStart) {
          loader.style.opacity = '1';
          loader.style.display = 'flex';
        }

        if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ) {
          setTimeout(() => {
            loader.style.opacity = '0';
            setTimeout(() => {
              loader.style.display = 'none';
              this.firstLoadDone = true;
            }, 400);
          }, 50);
        }
      }
    });
  }

  ngOnInit() {
    // Check session expiration on app initialization (with a small delay to ensure auth is initialized)
    setTimeout(() => {
      this.authService.checkAndHandleSessionExpiration();
    }, 1000);

    // Connect to Socket.IO if user is already logged in
    if (this.authService.isLoggedIn()) {
      console.log('🔌 [AppComponent] User is logged in, attempting socket connection');
      this.socketService.debugAuthData();
      this.socketService.connect();
      // Test connection after a short delay
      setTimeout(() => {
        this.socketService.testConnection();
        this.socketService.checkConnectionStatus();
      }, 2000);

      // Set up periodic session expiration check (every 1 minute for testing, change to 5 minutes in production)
      this.sessionCheckInterval = setInterval(() => {
        this.authService.checkAndHandleSessionExpiration();
      }, 1 * 60 * 1000); // Check every 1 minute for testing
    } else {
      console.log('🔌 [AppComponent] User not logged in, skipping socket connection');
    }

    // Load experience settings - subscribe to userLoggedIn to apply after API theme loads
    this.authService.userLoggedIn.subscribe(loggedIn => {
      if (loggedIn) {
        // Wait a bit for any API calls to complete, then apply local storage settings
        // This ensures local storage settings take priority over default API values
        setTimeout(() => {
          this.applyExperienceSettings();
        }, 500);

        // Start periodic session expiration check if not already running
        if (!this.sessionCheckInterval) {
          this.sessionCheckInterval = setInterval(() => {
            this.authService.checkAndHandleSessionExpiration();
          }, 1 * 60 * 1000); // Check every 1 minute for testing
        }
      } else {
        // If not logged in, apply immediately
        this.applyExperienceSettings();

        // Clear session check interval when user logs out
        if (this.sessionCheckInterval) {
          clearInterval(this.sessionCheckInterval);
          this.sessionCheckInterval = null;
        }
      }
    });
    
    // Also try to apply immediately if user is already logged in
    if (this.authService.isLoggedIn()) {
      setTimeout(() => {
        this.applyExperienceSettings();
      }, 500);
    } else {
      // If not logged in, apply immediately
      this.applyExperienceSettings();
    }
  }

  private applyExperienceSettings(): void {
    const savedSettings = localStorage.getItem('experienceSettings');
    if (savedSettings) {
      try {
        const savedSettingsObject = JSON.parse(savedSettings);
        console.log("Applying experienceSettings from localStorage:", savedSettingsObject);
        
        // Only update if we have valid values
        if (savedSettingsObject && (savedSettingsObject.colorScheme || savedSettingsObject.accentColor || savedSettingsObject.chartTheme)) {
          // Use theme.set() to ensure the signal change triggers the effect
          const currentTheme = this.themeService.theme();
          const updatedTheme = {
            ...currentTheme,
            colorScheme: savedSettingsObject.colorScheme ?? currentTheme.colorScheme,
            accentColor: savedSettingsObject.accentColor ?? currentTheme.accentColor,
            chartTheme: savedSettingsObject.chartTheme ?? currentTheme.chartTheme,
          };
          
          console.log("Updating theme from:", currentTheme, "to:", updatedTheme);
          this.themeService.theme.set(updatedTheme);
        }
      } catch (error) {
        console.error('Error parsing experienceSettings from localStorage:', error);
      }
    }
  }

  ngOnDestroy() {
    // Clear session check interval when component is destroyed
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }
  }
}
